"use client";

import { MicrosoftMark } from "@/components/os/shared/MicrosoftMark";
import { useRef, useState, type ReactNode } from "react";
import Link from "next/link";
import { signIn } from "next-auth/react";
import { z } from "zod";
import { safeCallbackUrl } from "@/lib/session/loginRedirect";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Field, FieldError, FieldLabel } from "@/components/ui/field";
import { Form, FormRefusal, useFormPending } from "@/components/ui/form";
import { Separator } from "@/components/ui/separator";

/**
 * Signing in. Almost everyone is in Aura's Microsoft tenant, so the Microsoft button is the page
 * and the password form is one link away (invited users outside the tenant, the break-glass
 * administrator); opening it is remembered per browser. Both routes end at the `callbackUrl`
 * proxy.ts set, so a deep link keeps its destination (lib/session/loginRedirect).
 */

/** Read at click time: keeps this component free of `useSearchParams`, which would need a Suspense boundary. */
function destination(): string {
  const raw = new URLSearchParams(window.location.search).get("callbackUrl");
  return safeCallbackUrl(raw, window.location.origin);
}

/** The password form's one schema: an address, and something in the password box. */
const CREDENTIALS = z.object({
  email: z.email("Enter a valid email address"),
  password: z.string().min(1, "Enter your password"),
});

/** The sign-in button: the sweep and the cross-fading words run while the form's save is out. */
function SignInButton() {
  const submitting = useFormPending();
  return (
    <Button
      type="submit"
      disabled={submitting}
      aria-busy={submitting}
      size="lg"
      className={`w-full disabled:opacity-100 ${submitting ? "aura-signin-sheen" : ""}`}
    >
      <SwappingLabel showBusy={submitting} idle="Sign in" busy="Signing in…" />
    </Button>
  );
}

/** Two labels stacked in one grid cell, cross-fading; both buttons are full width so nothing reflows while they swap. */
function SwappingLabel({ idle, busy, showBusy }: { idle: ReactNode; busy: ReactNode; showBusy: boolean }) {
  return (
    <span className="grid place-items-center">
      <span
        className={`col-start-1 row-start-1 flex items-center gap-3 transition-opacity duration-300 ${showBusy ? "opacity-0" : "opacity-100"}`}
        aria-hidden={showBusy}
      >
        {idle}
      </span>
      <span
        className={`col-start-1 row-start-1 flex items-center gap-3 transition-opacity duration-300 ${showBusy ? "opacity-100" : "opacity-0"}`}
        aria-hidden={!showBusy}
      >
        {busy}
      </span>
    </span>
  );
}

/** Microsoft's four squares, brightening in turn (reading order) while the redirect is in flight, instead of a spinner. */

/** `passwordLogin` is off unless AUTH_PASSWORD_LOGIN=on: Aura signs in with Microsoft. */
export function LoginForm({ passwordLogin = false }: { passwordLogin?: boolean }) {
  const [showPassword, setShowPassword] = useState(false);
  /** Never reset: the Microsoft button hands the browser to the tenant, and a slow redirect should not look idle again. */
  const [leavingForMicrosoft, setLeavingForMicrosoft] = useState(false);
  const emailRef = useRef<HTMLInputElement>(null);

  function hidePasswordForm() {
    setShowPassword(false);
  }

  function revealPasswordForm() {
    setShowPassword(true);
    // Opening a form and leaving the cursor elsewhere makes somebody click
    // twice to start typing.
    requestAnimationFrame(() => emailRef.current?.focus());
  }

  async function signInWithPassword(credentials: z.output<typeof CREDENTIALS>) {
    const result = await signIn("credentials", { ...credentials, redirect: false });
    if (result?.error) throw new FormRefusal({ password: ["Invalid email or password"] });
    window.location.href = destination();
    // Signed in: the form stays locked until the browser has gone to the page.
    await new Promise<never>(() => undefined);
  }

  return (
    <div className="mx-auto flex w-full max-w-sm flex-col">
      <Button
        type="button"
        variant="outline"
        disabled={leavingForMicrosoft}
        aria-busy={leavingForMicrosoft}
        onClick={() => {
          setLeavingForMicrosoft(true);
          void signIn("microsoft-entra-id", { callbackUrl: destination() });
        }}
        size="lg"
        // Stays at full strength while it is busy: the sweep across the logo is the progress, and a
        // faded button would read as refused rather than working.
        className="w-full disabled:opacity-100"
      >
        <SwappingLabel
          showBusy={leavingForMicrosoft}
          idle={
            <>
              <MicrosoftMark /> Sign in with Microsoft
            </>
          }
          busy={
            <>
              <MicrosoftMark animated /> Taking you to Microsoft…
            </>
          }
        />
      </Button>

      {!passwordLogin ? null : showPassword ? (
        <>
          <div className="my-6 flex items-center gap-3">
            <Separator className="flex-1" />
            <span className="text-caption font-medium text-muted-foreground">or</span>
            <Separator className="flex-1" />
          </div>

          <Form
            schema={CREDENTIALS}
            onValid={signInWithPassword}
            name="The sign-in"
            aria-label="Sign in with a password"
          >
            <Field name="email">
              <FieldLabel>Work email</FieldLabel>
              <Input ref={emailRef} inputMode="email" autoComplete="username" placeholder="you@auraretrofit.co.uk" />
              <FieldError />
            </Field>
            <Field name="password">
              <FieldLabel>Password</FieldLabel>
              <Input type="password" autoComplete="current-password" placeholder="••••••••" />
              <FieldError />
              <Link
                href="/forgot-password"
                className="justify-self-end text-small font-medium text-primary-hover underline-offset-4 hover:underline dark:text-primary"
              >
                Forgot password?
              </Link>
            </Field>
            <SignInButton />
          </Form>

          <Button type="button" variant="ghost" size="sm" onClick={hidePasswordForm} className="mt-4 self-center">
            Hide this — I sign in with Microsoft
          </Button>
        </>
      ) : (
        <Button type="button" variant="ghost" size="sm" onClick={revealPasswordForm} className="mt-5 self-center">
          Use an email address and password instead
        </Button>
      )}
    </div>
  );
}
